/**
 * What can be done to a bug, at the pointer: send it back to the jar, hand it to somebody, mark it
 * in progress or ready for review, close it, reopen it, cancel it.
 *
 * Only what makes sense from where the bug is: a waiting bug is not offered "back to waiting", a
 * closed one is offered "reopen" and little else.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ArchiveRestore, CheckCircle2, ChevronRight, Eye, Hammer, RotateCcw, Search, UserPlus, XCircle } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import type { BugState } from '../lib/types'

export type BugAction = 'wait' | 'start' | 'review' | 'complete' | 'reopen' | 'assign' | 'cancel'

export interface BugMenuBug {
  taskId: string
  externalId: string
  title: string
  state: BugState
  developerId: string | null
}

export function BugMenu({
  bug,
  x,
  y,
  developers,
  onAction,
  onOpen,
  onClose,
}: {
  bug: BugMenuBug
  x: number
  y: number
  developers: Array<{ id: string; name: string }>
  onAction: (action: BugAction, developerId?: string) => void
  onOpen: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const ref = useRef<HTMLDivElement>(null)
  const [assigning, setAssigning] = useState(false)
  const [position, setPosition] = useState({ left: x, top: y })

  // Kept on screen: a menu opened near the right or bottom edge opens the other way.
  useLayoutEffect(() => {
    const box = ref.current?.getBoundingClientRect()
    if (!box) return
    setPosition({
      left: Math.max(8, Math.min(x, window.innerWidth - box.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - box.height - 8)),
    })
  }, [x, y, assigning])

  useEffect(() => {
    const away = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('pointerdown', away, true)
    window.addEventListener('keydown', key)
    window.addEventListener('resize', onClose)
    return () => {
      window.removeEventListener('pointerdown', away, true)
      window.removeEventListener('keydown', key)
      window.removeEventListener('resize', onClose)
    }
  }, [onClose])

  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [])

  const state = bug.state
  const closed = state === 'Completed'
  const items: Array<{ action: BugAction; icon: typeof Eye; show: boolean; danger?: boolean }> = [
    { action: 'wait', icon: ArchiveRestore, show: !closed && (state !== 'Waiting' || bug.developerId != null) },
    { action: 'start', icon: Hammer, show: !closed && state !== 'InProgress' && bug.developerId != null },
    { action: 'review', icon: Search, show: !closed && state !== 'ReadyForReview' },
    { action: 'complete', icon: CheckCircle2, show: !closed },
    { action: 'reopen', icon: RotateCcw, show: closed || state === 'ReadyForReview' },
  ]

  return (
    <div
      ref={ref}
      className="bug-menu"
      role="menu"
      aria-label={`${bug.externalId} ${bug.title}`}
      style={{ left: position.left, top: position.top }}
      onContextMenu={(event) => event.preventDefault()}
    >
      <div className="bug-menu__head">
        <span className="mono bold small">{bug.externalId}</span>
        <span className="tiny muted truncate">{bug.title}</span>
      </div>

      <button type="button" role="menuitem" className="bug-menu__item" onClick={onOpen}>
        <Eye size={14} aria-hidden /> {t('bugMenu.open')}
      </button>

      <div className="bug-menu__sep" />

      {items.filter((item) => item.show).map(({ action, icon: Icon }) => (
        <button key={action} type="button" role="menuitem" className="bug-menu__item" onClick={() => onAction(action)}>
          <Icon size={14} aria-hidden /> {t(`bugMenu.${action}`)}
        </button>
      ))}

      {!closed && (
        <>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={assigning}
            className="bug-menu__item"
            onClick={() => setAssigning(!assigning)}
          >
            <UserPlus size={14} aria-hidden /> {t('bugMenu.assign')}
            <ChevronRight size={13} aria-hidden className={`bug-menu__chevron${assigning ? ' bug-menu__chevron--open' : ''}`} />
          </button>
          {assigning && (
            <div className="bug-menu__people" role="group">
              {developers.map((developer) => (
                <button
                  key={developer.id}
                  type="button"
                  role="menuitem"
                  className={`bug-menu__item bug-menu__item--person${developer.id === bug.developerId ? ' bug-menu__item--current' : ''}`}
                  disabled={developer.id === bug.developerId}
                  onClick={() => onAction('assign', developer.id)}
                >
                  {developer.name}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {!closed && (
        <>
          <div className="bug-menu__sep" />
          <button type="button" role="menuitem" className="bug-menu__item bug-menu__item--danger" onClick={() => onAction('cancel')}>
            <XCircle size={14} aria-hidden /> {t('bugMenu.cancel')}
          </button>
        </>
      )}
    </div>
  )
}
